import React, { ReactNode } from 'react';
import { FormattedDate, FormattedNumber, FormattedTime } from 'react-intl';
import { styled } from '@linaria/react';
import { Divider } from '@daedalus/atlas/Divider';
import { ContentWrapper } from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { freeCancellationPenalty, getDateTimeAndTimezone, RefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { formatTimeZone } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { CancellationPenalty } from '@daedalus/core/src/offer/types/offer';
import { convertToDifferentTimeZone } from '@daedalus/core/src/utils/date';
interface PenaltyContainerProps {
  date: ReactNode;
  timezone: string;
  refundType?: RefundType;
  refundAmount: ReactNode;
  amount: ReactNode;
  id: string | number;
  nonRefundable?: boolean;
  dateTime: string;
  hasChargeLaterRefundPolicy?: boolean;
}
interface PenaltyDetailsProps {
  id: string | number;
  penalty: CancellationPenalty;
  totalOfferPrice: number;
  displayCurrencyCode: string;
  hotelTimeZone?: string;
  hasChargeLaterRefundPolicy?: boolean;
  currencyStyle?: 'symbol' | 'code';
}
interface NonRefundablePeriodProps {
  date: string;
  totalOfferPrice: number;
  currency: string;
  displayCurrencyCode: string;
  hotelTimeZone?: string;
  currencyStyle?: 'symbol' | 'code';
}
const _exp = /*#__PURE__*/() => Text;
const CapitalizedText = /*#__PURE__*/styled(_exp())({
  name: "CapitalizedText",
  class: "cxh75f5",
  propsAsIs: true
});
const ScheduleRefundWrapper = /*#__PURE__*/styled('div')({
  name: "ScheduleRefundWrapper",
  class: "swklsun",
  propsAsIs: false
});
export const PenaltyContainer = ({
  date,
  timezone,
  refundType,
  refundAmount,
  amount,
  id,
  nonRefundable,
  dateTime,
  hasChargeLaterRefundPolicy
}: PenaltyContainerProps) => {
  const freeCancellationMessage = hasChargeLaterRefundPolicy ? <ScheduleRefundWrapper>
      <FormattedMessageWrapper id="cancellationPenalties.fullRefundScheduledPayment" defaultMessage="Full refund of scheduled payment" />
    </ScheduleRefundWrapper> : <FormattedMessageWrapper id="cancellationPenalties.fullRefund" defaultMessage="Full refund" />;
  return <div data-id={id}>
      <ContentWrapper paddingY="s500">
        <Grid container justify="space-between" spacing="s000" alignItems="center" gap="s500">
          <Grid>
            <Text variant="labelM">{date}</Text>
          </Grid>
          <Grid>
            {refundType === RefundType.freeCancellation ? <Text variant="labelM" colorPath="content.featured.c500">
                <FormattedMessageWrapper id="cancellationPenalties.freeCancellation" defaultMessage="Free cancellation" />
              </Text> : nonRefundable && !amount ? <Text variant="labelM">
                <Flex.Row gap="s100" alignItems="center">
                  <FormattedMessageWrapper id="cancellationPenalties.noRefund" defaultMessage="No" />
                  <FormattedMessageWrapper id="cancellationPenalties.refund" defaultMessage="refund" />
                </Flex.Row>
              </Text> : <Text variant="labelM">{amount}</Text>}
          </Grid>
        </Grid>
        <Grid container justify="space-between">
          <Grid>
            <CapitalizedText variant="bodyS" colorPath="content.neutral.c600">
              <FormattedMessageWrapper defaultMessage="{time} (GMT{timezone})" id="cancellationPenalties.time" values={{
              time: <FormattedTime value={dateTime} hour="2-digit" minute="2-digit" hourCycle="h23" />,
              timezone
            }} />
            </CapitalizedText>
          </Grid>
          <Grid>
            {refundType === RefundType.freeCancellation ? <Text variant="bodyS" colorPath="content.featured.c500">
                {freeCancellationMessage}
              </Text> : refundAmount ? <Grid container gap="s100" spacing="s000">
                <Text variant="bodyS" colorPath="content.neutral.c600">
                  {refundAmount}
                </Text>
                <Text variant="bodyS" colorPath="content.neutral.c600">
                  <FormattedMessageWrapper id="cancellationPenalties.refund" defaultMessage="refund" />
                </Text>
              </Grid> : null}
          </Grid>
        </Grid>
      </ContentWrapper>
      {!nonRefundable && <Divider />}
    </div>;
};
export const PenaltyDetails = ({
  id,
  penalty,
  totalOfferPrice,
  hotelTimeZone,
  hasChargeLaterRefundPolicy,
  displayCurrencyCode,
  currencyStyle
}: PenaltyDetailsProps) => {
  const {
    end: penaltyEndDate
  } = penalty;
  const penaltyDate = hotelTimeZone ? convertToDifferentTimeZone(penaltyEndDate, hotelTimeZone) : penaltyEndDate;
  const dateTime = getDateTimeAndTimezone(penaltyDate);
  const hasFreeCancellation = freeCancellationPenalty(penalty);
  const penaltyRefundType = hasFreeCancellation ? RefundType.freeCancellation : RefundType.partialRefund;
  const {
    amount,
    currency
  } = penalty;
  const isCurrencyConsistent = currency === displayCurrencyCode;
  return <PenaltyContainer id={`penalty-${id}`} date={<FormattedMessageWrapper defaultMessage="Before {date}" id="beforeDeadline" values={{
    date: <FormattedDate value={dateTime.date} month="short" day="2-digit" year="numeric" />
  }} />} amount={<FormattedNumber value={Number(amount)} style="currency" currencyDisplay={currencyStyle} currency={currency} />} refundType={penaltyRefundType} timezone={formatTimeZone(dateTime.timeZone)} refundAmount={isCurrencyConsistent ? <FormattedNumber value={Number(totalOfferPrice - Number(amount))} style="currency" currencyDisplay={currencyStyle} currency={currency} /> : null} dateTime={dateTime.date} hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy} />;
};
export const NonRefundablePeriod = ({
  date,
  totalOfferPrice,
  currency,
  displayCurrencyCode,
  hotelTimeZone,
  currencyStyle
}: NonRefundablePeriodProps) => {
  const penaltyDate = hotelTimeZone ? convertToDifferentTimeZone(date, hotelTimeZone) : date;
  const dateTime = getDateTimeAndTimezone(penaltyDate);
  const isCurrencyConsistent = currency === displayCurrencyCode;
  return <PenaltyContainer id="nonRefundablePeriod" date={<FormattedMessageWrapper defaultMessage="After {date}" id="cancellationPenalties.afterDate" values={{
    date: <FormattedDate value={dateTime.date} month="short" day="2-digit" year="numeric" />
  }} />} amount={isCurrencyConsistent ? <div data-id="refundableCancellationPenalties.totalPrice">
            <FormattedNumber value={Number(totalOfferPrice)} style="currency" currencyDisplay={currencyStyle} currency={displayCurrencyCode} />
          </div> : null} timezone={formatTimeZone(dateTime.timeZone)} refundAmount={isCurrencyConsistent ? <FormattedMessageWrapper id="cancellationPenalties.noRefund" defaultMessage="No" /> : null} nonRefundable dateTime={dateTime.date} />;
};

require("./PenaltyDetails.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PenaltyDetails.tsx");